import { API_ENDPOINT } from "@/constants/appConstants";
import { SAVED_JOBS_QUERY_KEY } from "@/lib/queryKeys";
import { getJDUrlWithDomain } from "@/utils/jobs";
import { MessageCard } from "@monsterindia/global-ui-components";
import { JobCard } from "@monsterindia/seeker-jobs";
import { useQueryClient } from "@tanstack/react-query";
import dynamic from "next/dynamic";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { useSavedPageData } from "../";
import { useDashboardContext } from "../..";
import { getAdjustedHeight } from "../../utils/getDashboardAdjustedHeight";
import { jobSaveUnsaveAPI } from "../jobDescription/query";
import { useSavedJobs } from "../query";
import {
  DashboardContextProps,
  SavedJobAPIDataType,
  SavedPageContextProps,
  SavedPageDataType,
} from "../types";
import "./components/styles.css";
import Skeleton from "./skeleton";

const ApplyJob = dynamic<{
  jobData: SavedJobAPIDataType;
  siteProperties: sitePropsType | undefined;
  pageName: string;
  btnClass: string;
  isSyndicateQuickApplyEnabled: boolean;
  isLoggedIn: boolean;
  isMobile: boolean;
  isJobApplied: boolean;
  divClass?: string;
  inputResponse?: (data: any) => void;
}>(() => import("@monsterindia/seeker-jobs").then((mod) => mod.ApplyJob), {
  ssr: false,
});

const SavedJobCard = () => {
  const { isMobile, host }: DashboardContextProps = useDashboardContext();
  const {
    savedJobData,
    setSavedJobData,
    handleEventTracking,
    jobUserInfoHandler,
    cleverTapPropertyValues,
  }: SavedPageContextProps = useSavedPageData();
  const [savedJobParams, setSavedJobParams] = useState({
    initialPage: 2,
    currentPage: 1,
    filterStatus: savedJobData.selectedFilter?.[0]?.id || "",
    isFetchingEnabled: false,
    resetType: { value: "none" },
    loadingJobCard: true,
    lastDataLength: savedJobData.totalDataLength,
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const isSyndicateQuickApplyEnabled =
    savedJobData?.windowData?.isSyndicateQuickApplyEnabled || false;

  const queryClient = useQueryClient();

  const {
    data,
    isError,
    isFetchingNextPage,
    isFetching,
    refetch,
    fetchNextPage,
  } = useSavedJobs(
    savedJobParams.filterStatus,
    savedJobParams.isFetchingEnabled,
    savedJobParams.initialPage,
  );

  const saveUnsaveJobHandler = async (
    jobId: number,
    type: string,
    props: { callBackSuccess: boolean },
  ) => {
    let response: any;
    let responseMessage;
    if (type === "apiCall") {
      response = await jobSaveUnsaveAPI(
        jobId,
        savedJobData.savedJobs.includes(jobId) ? "unsave" : "save",
      );
      responseMessage = response?.successMessage;
    } else if (type == "callBack") {
      if (props.callBackSuccess) {
        responseMessage = "Job saved successfully";
      } else {
        responseMessage = "Job unsaved successfully";
      }
    }

    if (responseMessage == "Job saved successfully") {
      setSavedJobData((prevData: SavedPageDataType) => {
        const savedJobsModified = {
          ...prevData,
          savedJobs: [...(prevData?.savedJobs || []), jobId],
        };
        if (type === "apiCall")
          savedJobsModified["toastMessage"] = {
            text: "Job saved successfully",
            type: "success",
            show: true,
          };
        if (!prevData.saveUnsaveJobHandler)
          savedJobsModified["saveUnsaveJobHandler"] = saveUnsaveJobHandler;
        return savedJobsModified;
      });
    } else if (responseMessage == "Job unsaved successfully") {
      setSavedJobData((prevData: SavedPageDataType) => {
        const newSavedJobs = prevData?.savedJobs?.filter(
          (prevJobId: number) => prevJobId !== jobId,
        );
        const savedJobsModified = {
          ...prevData,
          savedJobs: [...(newSavedJobs || [])],
        };
        if (type === "apiCall")
          savedJobsModified["toastMessage"] = {
            text: "Job unsaved successfully",
            type: "success",
            show: true,
          };
        if (!prevData.saveUnsaveJobHandler)
          savedJobsModified["saveUnsaveJobHandler"] = saveUnsaveJobHandler;
        return savedJobsModified;
      });
    } else {
      setSavedJobData((prevData: SavedPageDataType) => {
        const savedJobsModified = {
          ...prevData,
        };
        if (type === "apiCall")
          savedJobsModified["toastMessage"] = {
            text: "Job save unsuccessfully",
            type: "error",
            show: true,
          };
        return savedJobsModified;
      });
      console.log("Error in saving/unsaving job");
    }
  };

  const updateJobApplicationState = (
    jobId: string,
    isApplied: boolean,
    indexVal: number | null,
  ) => {
    setSavedJobData((prevData: SavedPageDataType) => {
      const responseData = { ...prevData };
      const jobIndex = !indexVal
        ? responseData?.data?.findIndex(
            (job: SavedJobAPIDataType) => job?.id === jobId,
          )
        : indexVal;

      if (jobIndex >= 0) {
        if (responseData?.data?.[jobIndex]) {
          responseData.data[jobIndex] = {
            ...responseData.data[jobIndex],
            isApplied: true,
          };

          window?.dataLayer?.push({
            event: "Apply_Success",
            CriteoJobID: responseData.data[jobIndex].kiwiJobId,
          });
        }
      } else {
        console.error("Job not found in search results:", jobId);
      }
      return responseData;
    });
  };

  const handlePaginationScroll = () => {
    const scrollRef = !isError
      ? itemRefs.current?.[savedJobParams.lastDataLength - 1]
      : itemRefs.current?.[savedJobParams.lastDataLength - 3];
    if (scrollRef) {
      scrollRef?.scrollIntoView();
    }
  };

  const handleScroll = useCallback(
    async (event: React.UIEvent<HTMLDivElement> | Event) => {
      if (isFetchingNextPage || isFetching) return;

      const { scrollTop, scrollHeight, clientHeight } = !isMobile
        ? (event.currentTarget as HTMLDivElement)
        : ((event.target as Document).scrollingElement as HTMLElement);
      if (scrollTop + clientHeight >= scrollHeight - 100) {
        if (
          savedJobData.totalDataLength >= savedJobData.totalPagingLength ||
          savedJobData.isLoadingJobDesc
        )
          return;

        setSavedJobParams((prev) => ({
          ...prev,
          currentPage: prev.currentPage + 1,
          loadingJobCard: true,
          lastDataLength: savedJobData.totalDataLength,
        }));
        fetchNextPage();
      }
    },
    [
      isFetchingNextPage,
      isFetching,
      fetchNextPage,
      savedJobParams,
      savedJobData,
    ],
  );

  const resetAndRefetch = () => {
    queryClient.removeQueries({
      queryKey: [
        SAVED_JOBS_QUERY_KEY,
        savedJobParams.filterStatus,
        savedJobParams.initialPage,
      ],
      exact: true,
    });
    setSavedJobParams((prev) => {
      return {
        ...prev,
        initialPage: 1,
        currentPage: 1,
        filterStatus: savedJobData.selectedFilter?.[0]?.id || "",
        resetType: { value: "filterClick" },
        loadingJobCard: true,
        lastDataLength: savedJobData.totalDataLength,
      };
    });
  };

  const adjustHeight = useCallback(() => {
    if (containerRef.current) {
      const remainingHeight = getAdjustedHeight("saved");
      containerRef.current.style.height = `${remainingHeight}px`;
    }
  }, [containerRef.current, isMobile]);

  useEffect(() => {
    if (!isMobile) {
      adjustHeight();
      window.addEventListener("resize", adjustHeight);
      return () => {
        window.removeEventListener("resize", adjustHeight);
      };
    } else {
      window.addEventListener("scroll", handleScroll, { passive: true });
      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    }
  }, [
    isFetchingNextPage,
    isFetching,
    fetchNextPage,
    savedJobParams,
    savedJobData,
    isMobile,
  ]);

  useEffect(() => {
    if (data) {
      const newFilters =
        data.pages[data.pages.length - 1]?.filters?.filterMap || {};
      const newMetaData = data.pages[data.pages.length - 1]?.meta || {};
      const newJobs = data.pages[data.pages.length - 1]?.data || [];
      const newSavedJobs = data.pages[data.pages.length - 1]?.data
        ?.filter((val: SavedJobAPIDataType, index: number) => val?.jobId)
        .map((val: SavedJobAPIDataType, index: number) => val.jobId);
      jobUserInfoHandler(
        savedJobParams.currentPage == 1
          ? [...newJobs]
          : [...(savedJobData?.data || []), ...newJobs],
      );
      setSavedJobData((prevData: SavedPageDataType) => {
        const savedJobsModified = {
          ...prevData,
          data:
            savedJobParams.currentPage == 1
              ? [...newJobs]
              : [...(prevData?.data || []), ...newJobs],
          filters: { ...newFilters },
          meta: { ...newMetaData },
          filterStatusPresent: false,
          savedJobs: newSavedJobs,
        };
        if (!prevData?.autoApplyData?.enabled)
          savedJobsModified["autoApplyData"] = {};
        return savedJobsModified;
      });
    } else if (isError) {
      setSavedJobData((prevData: SavedPageDataType) => {
        const savedJobsModified = {
          ...prevData,
          data: { ...prevData.data },
        };
        if (!prevData?.autoApplyData?.enabled)
          savedJobsModified["autoApplyData"] = {};
        return savedJobsModified;
      });
    }
  }, [data, isError]);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      if (isFetchingNextPage) {
        container.classList.remove("overflow-y-scroll");
        container.classList.add("overflow-y-hidden");
      } else {
        setTimeout(() => {
          container.classList.remove("overflow-y-hidden");
          container.classList.add("overflow-y-scroll");
        }, 200);
      }
    }
  }, [isFetchingNextPage]);

  useEffect(() => {
    if (savedJobParams.initialPage === 1) {
      refetch();
    }
  }, [savedJobParams.resetType]);

  useEffect(() => {
    if (
      savedJobData.selectedFilter &&
      Array.isArray(savedJobData.selectedFilter)
    ) {
      if (isMobile) {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      } else {
        const container = containerRef.current;
        if (container) {
          container.scrollTo({
            top: 0,
            behavior: "smooth",
          });
        }
      }
      resetAndRefetch();
    }
  }, [savedJobData.selectedFilter, isMobile]);

  useEffect(() => {
    if (savedJobData.isLoadingJobDesc && !savedJobParams.loadingJobCard) {
      setTimeout(() => {
        setSavedJobData((prevData: SavedPageDataType) => {
          const modifiedData = { ...prevData, isLoadingJobDesc: false };
          if (!prevData.leftSideRef) modifiedData.leftSideRef = containerRef;
          if (!prevData.saveUnsaveJobHandler)
            modifiedData.saveUnsaveJobHandler = saveUnsaveJobHandler;
          return modifiedData;
        });
      }, 200);
    } else if (
      !savedJobData.isLoadingJobDesc &&
      !savedJobParams.loadingJobCard
    ) {
      handlePaginationScroll();
    }
  }, [savedJobParams.loadingJobCard]);

  useEffect(() => {
    if (savedJobParams.loadingJobCard && !savedJobData.newDataLoader) {
      setTimeout(() => {
        setSavedJobParams((prev) => ({
          ...prev,
          loadingJobCard: false,
        }));
      }, 200);
    } else if (!savedJobData.newDataLoader) {
      setTimeout(() => {
        setSavedJobData((prevData: SavedPageDataType) => {
          return { ...prevData, isLoadingJobDesc: false };
        });
      }, 200);
    }
  }, [savedJobData.jobDescIndex, savedJobData.data]);

  useEffect(() => {
    if (isMobile) return;
    const container = containerRef.current;
    if (!container) return;

    const onWheel = (e: WheelEvent) => {
      const maxScroll = container.scrollHeight - container.clientHeight;
      if (maxScroll <= 0) return;

      e.preventDefault();
      let next = container.scrollTop + e.deltaY;
      if (next < 0) next = 0;
      if (next > maxScroll) next = maxScroll;
      container.scrollTop = next;
    };

    window.addEventListener("wheel", onWheel, { passive: false });
    return () => window.removeEventListener("wheel", onWheel);
  }, [containerRef, isMobile]);

  return (
    <div
      ref={containerRef}
      className={`scrollbar-hide saved-jobs-container flex flex-col pb-3`}
      onScroll={handleScroll}
      id="saved-jobs-container"
    >
      {!savedJobParams.loadingJobCard && !savedJobData.filterDisable && (
        <div className={`${isMobile ? "!mb-2" : "!mb-3"}`}>
          <p className="!ml-[20px] overflow-hidden truncate text-xs font-normal leading-4 text-[#59566C]">
            Showing{" "}
            <span className="text-xs font-medium text-[#17142A]">
              {savedJobData.totalPagingLength}{" "}
              {savedJobData.totalPagingLength > 1 ? "jobs" : "job"}
            </span>
          </p>
        </div>
      )}
      {savedJobData.data &&
        Array.isArray(savedJobData.data) &&
        savedJobData.data.map((data: SavedJobAPIDataType, index: number) => {
          let fullName;
          if (index == 3) {
            fullName =
              savedJobData?.profilePersonalDetails?.personalDetailSection
                ?.personalDetails?.fullName || "";
          }
          return (
            <div
              key={data?.companyId || index}
              className={`${savedJobParams.loadingJobCard || savedJobData.filterDisable ? "hidden" : ""} ${isMobile ? "mb-3" : "mb-5"}`}
              ref={(el) => {
                itemRefs.current[index] = el;
              }}
            >
              {index == 3 ? (
                <a
                  href="/career-services/career-booster"
                  className="cursor-pointer"
                  target="_blank"
                >
                  <MessageCard
                    key={index}
                    bodyText={`${fullName ? `Hi ${fullName?.split(" ")[0]}, revamp ` : "Revamp "}your resume, plus rank higher to get more call-backs.`}
                    className="bg-message-card mb-[16px]"
                    iconAltText="Rocket Nudge"
                    leftIconSrc={`https://${API_ENDPOINT("").media.rocketNudge}`}
                  />
                </a>
              ) : null}
              <div>
                <JobCard
                  key={data?.companyId || index}
                  cardClickHandler={(url: string) => {
                    setSavedJobData((prevData: SavedPageDataType) => {
                      return {
                        ...prevData,
                        isLoadingJobDesc: true,
                        jobDescIndex: { selectedIndex: index },
                        showMobileModal: isMobile ? true : false,
                      };
                    });
                    handleEventTracking({
                      eventName: "Job Card Clicked",
                      eventType: "eventPush",
                      eventProps: {
                        page_name:
                          cleverTapPropertyValues[
                            savedJobData.activeTab as keyof typeof cleverTapPropertyValues
                          ],
                      },
                    });
                    window.open(url, isMobile ? "_self" : "_blank");
                  }}
                  isSyndicatedQuickApplyEnabled={isSyndicateQuickApplyEnabled}
                  isMobile={isMobile}
                  jobData={{
                    ...data,
                    jdUrl: getJDUrlWithDomain(data.jdUrl, host),
                  }}
                  saveUnsaveJobHandler={() => {
                    saveUnsaveJobHandler(data.jobId, "apiCall", {
                      callBackSuccess: false,
                    });
                  }}
                  applyBtnComponent={
                    <ApplyJob
                      key={data?.companyId || index}
                      jobData={data}
                      siteProperties={savedJobData.sitePropsProperties}
                      isSyndicateQuickApplyEnabled={
                        isSyndicateQuickApplyEnabled
                      }
                      pageName="Dashboard"
                      btnClass="rounded-full bg-surface-primary-pressed !font-semibold border-0 text-xs text-content-strong py-2 px-3"
                      isLoggedIn={true}
                      isMobile={isMobile}
                      divClass="w-1/2 md:!w-fit"
                      isJobApplied={
                        data?.isApplied ||
                        savedJobData?.userInfo?.appliedJobIds?.includes(
                          data?.id || "",
                        ) ||
                        false
                      }
                      inputResponse={(inputData: any) => {
                        if (inputData.status === 200) {
                          updateJobApplicationState(data.id ?? "", true, index);
                        }
                      }}
                    />
                  }
                  updateIsSaved={savedJobData.savedJobs.includes(data?.jobId)}
                  disableCompanyNameLink={true}
                  pageName="Jobs"
                  siteContext={data?.siteContext}
                  className="hover:shadow-job-card-hover border-jobCardBorder bg-surface-primary-normal shadow-job-card relative w-auto cursor-pointer border border-solid"
                  fullView={true}
                  btnStyleProps="!hover:bg-[#EEEEEE] !bg-surface-primary-disabled"
                />
              </div>
            </div>
          );
        })}
      {Array(savedJobData.selectedFilter?.[0]?.count || 10)
        .fill(0)
        .map((_, index) => {
          return (
            <div
              className={`${savedJobParams.loadingJobCard || savedJobData.filterDisable ? "" : "hidden"} ${isMobile ? "mb-3" : "mb-5"}`}
              id={index + "-skeleton"}
            >
              <Skeleton />
            </div>
          );
        })}
    </div>
  );
};

export default React.memo(SavedJobCard);
